import * as Sentry from '@sentry/react';

const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;

/**
 * The SDK namespaces its global-handler mechanisms (`@sentry/browser`'s
 * `integrations/globalhandlers` module), so an unhandled rejection
 * arrives as `auto.browser.global_handlers.onunhandledrejection` rather than the
 * bare `onunhandledrejection` this filter was first written against. Match the
 * suffix so both the legacy and namespaced spellings are covered.
 */
function isUnhandledRejectionMechanism(type: string | undefined): boolean {
    if (!type) return false;
    return type === 'onunhandledrejection' || type.endsWith('.onunhandledrejection');
}

/**
 * The SDK's template for a rejection whose reason was a *primitive*
 * (`_eventFromRejectionWithPrimitive` in that same module). That path builds
 * the exception by hand and never sets `mechanism.synthetic`, so it needs its
 * own clause.
 */
const PRIMITIVE_REJECTION_PREFIX = 'Non-Error promise rejection captured with value:';

/**
 * Drop unhandled promise rejections that carry no Error — PLINKROMATIC-3, and
 * PLINKROMATIC-C / PLINKROMATIC-D, which escaped the original filter.
 *
 * The discriminator is structural, not textual. `eventFromUnknownInput`
 * (`@sentry/browser`'s `eventbuilder` module) returns early via `eventFromError` for a
 * real `Error` and only stamps `mechanism.synthetic = true` on the branches that
 * *manufacture* an exception from a plain object or a DOM `Event`. So
 * `synthetic` is precisely "the SDK invented this exception because the rejected
 * value was not an Error" — it can never be set for a genuine thrown Error, which
 * is what the previous message-matching could not guarantee.
 *
 * The superseded filter keyed on `mechanism.type === 'onunhandledrejection'` and
 * the literal `"Event 'Event'"`. Neither matches SDK 10.x: the mechanism type is
 * namespaced (above) and the message templates use backticks and the rejected
 * value's real constructor name (``Event `CustomEvent` (type=unhandledrejection)
 * captured as promise rejection``). It therefore matched nothing.
 */
export function isNonErrorPromiseRejection(event: Sentry.ErrorEvent): boolean {
    const values = event.exception?.values;
    if (!values?.length) return false;

    return values.some(value => {
        if (!isUnhandledRejectionMechanism(value.mechanism?.type)) return false;
        if (value.mechanism?.synthetic === true) return true;
        return value.value?.startsWith(PRIMITIVE_REJECTION_PREFIX) ?? false;
    });
}

export function initSentry() {
    if (!dsn) return;

    Sentry.init({
        dsn,
        release: process.env.NEXT_PUBLIC_GIT_SHA || undefined,
        environment: process.env.NEXT_PUBLIC_APP_ENV || 'production',
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration(),
        ],
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
        beforeSend(event) {
            if (isNonErrorPromiseRejection(event)) {
                return null;
            }
            return event;
        },
    });
}
